import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import { compressImage } from '../../../utils/images';
import { useImage } from '../../../hooks/image';
import Grid2 from '@mui/material/Unstable_Grid2';
import { nanoid } from 'nanoid';

interface ImageUploaderProps {
  onUploaded: (url: string) => void;
  submitLabel?: string;
  isSubmitLoading?: boolean;
  defaultImageUrl?: string;
  disabled?: boolean;
}

function ImageUploader({ disabled, onUploaded, isSubmitLoading , submitLabel = "Upload", defaultImageUrl  }: ImageUploaderProps) {
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(defaultImageUrl ?? null);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null); // Success message state
  const { uploadImage } = useImage();
  const [updatedDefaultImageUrl, setUpdatedDefaultImageUrl] = useState(defaultImageUrl)



  // Handle file selection
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSuccessMessage(null);

    const selectedFile = event.target.files?.[0];

    if (!selectedFile) return;

    const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();

    // Check file type (client-side validation)
    if (!['png', 'jpeg', 'jpg'].includes(fileExtension || '')) {
      toast.error('Only PNG and JPEG/JPG images are allowed.')
      setSelectedImage(null);
      return;
    }

    const compressedFile = await compressImage(selectedFile, {
      maxSizeMB: 0.1,
      maxWidthOrHeight: 800,
    });

    setSelectedImage(compressedFile); // Set the selected file

    // Generate a preview URL for the selected image
    const objectUrl = URL.createObjectURL(compressedFile);
    setPreviewUrl(objectUrl);
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setUploading(true);
    setSuccessMessage(null); // Reset the success message before uploading

    try {
      // Replace with your actual API endpoint
      let imageUrl : string | null = null;

      if (selectedImage) {
        imageUrl = await uploadImage(selectedImage);
        if (!imageUrl) {
          throw new Error('Failed to upload the image. Please try again.');
        }
      }

      onUploaded(imageUrl ?? defaultImageUrl ?? '');
      setUpdatedDefaultImageUrl(imageUrl ?? defaultImageUrl ?? '')
    } catch (err) {
      toast.error('Failed to upload the image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const inputId = useMemo(()=>nanoid(8), []);

  return (
    <Box
      sx={{ maxWidth: 400, margin: 'auto', padding: 3, textAlign: 'center' }}
    >
      {/* Error and Success Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ marginBottom: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Preview Section */}
      <Box sx={{ marginBottom: 2 }}>
        {previewUrl ? (
          <Avatar
            src={previewUrl}
            sx={{
              width: 120,
              height: 120,
              borderRadius: '8px',
              marginX: 'auto',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: 120,
              height: 120,
              borderRadius: '8px',
              marginX: 'auto',
              backgroundColor: '#f0f0f0',
              border: '2px dashed #b0b0b0',
            }}
          />
        )}
        <Typography variant="body2" sx={{ color: '#777', marginTop: 1 }}>
          {previewUrl ? 'Preview of your image' : 'No image selected'}
        </Typography>
      </Box>

      {/* File Upload Button */}
      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        id={inputId}
      />

      <Grid2 container spacing={1}>
          <Grid2 xs={12} md={updatedDefaultImageUrl != previewUrl ? 6  : 12}>
          <label htmlFor={inputId}>
              <Button
              size={'small'}
              variant="outlined"
              fullWidth
              component="span"
              disabled={disabled}
              sx={{maxWidth:200}}
            >
              {(selectedImage || previewUrl) ? 'Change Image' : 'Choose Image'}
            </Button>
            </label>

          </Grid2>
          <Grid2 xs={12} md={6}>
          {updatedDefaultImageUrl != previewUrl && <Button
            size={'small'}
            variant="contained"
            onClick={handleSubmit}
            fullWidth
            disabled={uploading || disabled}
            sx={{maxWidth:200}}
          >
            {(uploading || isSubmitLoading) ? (
              <CircularProgress size={24} sx={{ color: 'white', marginRight: 2 }} />
            ) : (
               submitLabel
            )}
          </Button>}

        </Grid2>
        </Grid2>
        
    </Box>
  );
}

export default ImageUploader;
